import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { useOpenMonths } from '../utils/months';

// Full-month attendance matrix (employees × days). Present is the default and is
// not stored — only off / half-day / absent are recorded. Click a cell to cycle.
const CYCLE = { present: 'off', off: 'halfday', halfday: 'absent', absent: 'present' };
const LETTER = { present: '✓', off: 'O', halfday: 'H', absent: '✗' };
const CELLCLS = { present: 'att-present', off: 'att-off', halfday: 'att-half', absent: 'att-absent' };
const WD = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const Attendance = () => {
  const { can } = useAuth();
  const canEdit = can('employees:write');
  // Attendance feeds payroll, so it may only be edited in an OPEN accounting month —
  // the picker offers exactly those rather than letting a closed month be marked up
  // and rejected on save.
  const { months, current: currentMonth, loading: monthsLoading } = useOpenMonths();
  const [month, setMonth] = useState('');
  const [data, setData] = useState({ employees: [], monthDays: 30, marks: {} });
  const [changes, setChanges] = useState({});   // `${empId}:${date}` -> status
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [branch, setBranch] = useState('');
  // The topbar's branch picker governs this page too. It is matched on id
  // rather than name, so renaming a store does not quietly empty the sheet.
  const { locationId } = useLocationFilter();
  const [picked, setPicked] = useState(() => new Set());   // employee ids, highlighted while marking

  useEffect(() => {
    if (monthsLoading) return;
    setMonth((m) => m || currentMonth || '');
    if (!currentMonth) setLoading(false);
  }, [monthsLoading, currentMonth]);

  const load = useCallback(() => {
    if (!month) return;
    setLoading(true); setChanges({});
    api.get(`/employees/attendance/month?month=${month}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [month]);
  useEffect(load, [load]);

  const [y, m] = month.split('-').map(Number);
  const dateStr = (d) => `${month}-${String(d).padStart(2, '0')}`;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isFuture = (d) => dateStr(d) > todayStr;   // can't mark attendance ahead of time
  // Before someone joined and after their last day there is nothing to mark:
  // those days are greyed out and cannot be clicked. Returns why, or null.
  const spanOf = Object.fromEntries((data.employees || []).map((e) => [e.id, {
    join: e.joinDate ? String(e.joinDate).slice(0, 10) : null,
    exit: e.exitDate ? String(e.exitDate).slice(0, 10) : null,
  }]));
  const notEmployed = (empId, d) => {
    const sp = spanOf[empId]; const ds = dateStr(d);
    if (sp?.join && ds < sp.join) return `joined on ${sp.join}`;
    if (sp?.exit && ds > sp.exit) return `left on ${sp.exit}`;
    return null;
  };
  const statusOf = (empId, d) => {
    const key = `${empId}:${dateStr(d)}`;
    if (key in changes) return changes[key];
    return data.marks?.[empId]?.[dateStr(d)] || 'present';
  };
  const cycle = (empId, d) => {
    if (!canEdit || isFuture(d) || notEmployed(empId, d)) return;
    const cur = statusOf(empId, d);
    setChanges((c) => ({ ...c, [`${empId}:${dateStr(d)}`]: CYCLE[cur] }));
  };
  const dirty = Object.keys(changes).length;

  const save = async () => {
    setSaving(true);
    try {
      const records = Object.entries(changes).map(([k, status]) => {
        const i = k.indexOf(':'); return { employeeId: Number(k.slice(0, i)), date: k.slice(i + 1), status };
      });
      const { data: r } = await api.post('/employees/attendance/bulk', { records });
      toast.success(`Saved — ${r.saved} exception(s)${r.cleared ? `, ${r.cleared} cleared` : ''}`);
      if (r.locked) toast(`${r.locked} change(s) skipped — payroll already finalized for that month`, { icon: '🔒' });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  // Filtered in the page rather than the request: the month's marks are already
  // loaded, so narrowing the list should be instant and reversible.
  const atLocation = useMemo(
    () => (locationId
      ? (data.employees || []).filter((e) => String(e.locationId || '') === String(locationId))
      : (data.employees || [])),
    [data.employees, locationId],
  );
  const branches = useMemo(
    () => Array.from(new Set(atLocation.map((e) => e.branch).filter(Boolean))).sort(),
    [atLocation],
  );
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return atLocation.filter((e) => {
      if (branch && (e.branch || '') !== branch) return false;
      if (!q) return true;
      return `${e.name} ${e.code || ''}`.toLowerCase().includes(q);
    });
  }, [atLocation, query, branch]);

  const togglePick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  const days = Array.from({ length: data.monthDays }, (_, i) => i + 1);
  const totals = (empId) => {
    const t = { off: 0, halfday: 0, absent: 0 };
    for (const d of days) { if (notEmployed(empId, d)) continue; const s = statusOf(empId, d); if (t[s] != null) t[s]++; }
    return t;
  };

  return (
    <div>
      <div className="section-head">
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <label className="muted small">Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 170 }} disabled={!months.length}>
            {!months.length && <option value="">No open month</option>}
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input className="att-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or code…" />
          {!locationId && (
            <select value={branch} onChange={(e) => setBranch(e.target.value)} style={{ width: 170 }}>
              <option value="">All branches</option>
              {branches.map((b2) => <option key={b2} value={b2}>{b2}</option>)}
            </select>
          )}
          <span className="muted small">
            {shown.length === data.employees.length
              ? `${data.employees.length} employees`
              : `${shown.length} of ${data.employees.length}`}
          </span>
          {locationId && shown.length === 0 && data.employees.length > 0 && (
            <span className="muted small">— nobody is posted to this branch</span>
          )}
          {(query || branch) && <button className="btn btn-outline btn-sm" onClick={() => { setQuery(''); setBranch(''); }}>Clear</button>}
        </div>
        {canEdit && <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : (dirty ? `Save (${dirty})` : 'Saved')}</button>}
      </div>
      <p className="muted small" style={{ marginTop: '-.4rem' }}>
        <span className="att-legend att-present">✓</span> present (default) · click a cell to cycle{' '}
        <span className="att-legend att-off">O</span> off ·{' '}
        <span className="att-legend att-half">H</span> half-day ·{' '}
        <span className="att-legend att-absent">✗</span> absent. Click a name to highlight that row.
      </p>

      {!loading && !month && (
        <div className="card card-pad empty">No accounting month is open. Open one in Finance › Month close to mark attendance.</div>
      )}
      {loading ? <div className="spinner" /> : month && (
        <div className="table-wrap att-wrap">
          <table className="grid att-grid">
            <thead>
              <tr>
                <th className="att-name">Employee{picked.size > 0 && (
                  <button className="btn btn-outline btn-sm att-clear-pick" onClick={() => setPicked(new Set())} title="Clear the selection">
                    {picked.size} selected ✕
                  </button>
                )}</th>
                <th className="att-branch">Branch</th>
                {days.map((d) => {
                  const wd = new Date(y, m - 1, d).getDay();
                  return <th key={d} className={`${wd === 0 ? 'att-sun' : ''}${isFuture(d) ? ' att-future' : ''}`} title={dateStr(d)}>{d}<br /><span className="att-wd">{WD[wd]}</span></th>;
                })}
                <th className="att-off">O</th><th className="att-half">H</th><th className="att-absent">A</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => {
                const t = totals(e.id);
                return (
                  <tr key={e.id} className={picked.has(e.id) ? 'att-picked' : ''}>
                    {/* The name itself still opens the employee; the space around
                        it selects the row, which is where the pointer already is. */}
                    <td className="att-name" onClick={() => togglePick(e.id)} title="Click to highlight this row">
                      <Link to={`/employees/${e.id}`} className="link-strong" onClick={(ev) => ev.stopPropagation()}>{e.name}</Link>{e.left && <span className="badge badge-gray" style={{ marginLeft: 4 }} title={e.exitDate ? `Left on ${e.exitDate}` : "No longer employed"}>left</span>}
                    </td>
                    <td className="att-branch muted" onClick={() => togglePick(e.id)}>{e.branch || '—'}</td>
                    {days.map((d) => {
                      if (isFuture(d)) return <td key={d} className="att-future" title={dateStr(d)}></td>;
                      const why = notEmployed(e.id, d);
                      if (why) return <td key={d} className="att-left" title={`${e.name} ${why}`}></td>;
                      const s = statusOf(e.id, d);
                      return <td key={d} className={`att-cell ${CELLCLS[s]}`} onClick={() => cycle(e.id, d)} title={`${e.name} · ${dateStr(d)}: ${s}`}>{LETTER[s]}</td>;
                    })}
                    <td>{t.off || ''}</td><td>{t.halfday || ''}</td><td>{t.absent || ''}</td>
                  </tr>
                );
              })}
              {!data.employees.length && <tr><td colSpan={days.length + 4} className="empty">No active employees.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Attendance;
